// src/hooks/usePriceQuotesData.js

import { useState, useEffect, useCallback } from 'react';
import { message } from 'antd';
import { paginationEstimates } from '~/services/hooks/estimates';
import { paginationSubVendors } from '~/services/hooks/subVendors';
import { paginationLaborTypes } from '~/services/hooks/laborTypes';
import { findAllEstimateSubVendors, updateEstimateSubVendor } from '~/services/hooks/estimateSubVendors';


/**
 * Hook para gerenciar o estado e a lógica de negócio do componente PriceQuotes
 * @param {number|string} projectId
 */
export function usePriceQuotesData(projectId) {
  const [loading, setLoading] = useState(false);
  const [estimateData, setEstimateData] = useState(null);
  const [allSubVendors, setAllSubVendors] = useState([]);
  const [allLaborTypes, setAllLaborTypes] = useState([]);
  const [winners, setWinners] = useState([]);
  const [showType, setShowType] = useState(1); // null?
  const [selectedEstimateSubVendors, setSelectedEstimateSubVendors] = useState([]);
  const [completeEstimate, setCompleteEstimate] = useState([]);
  const [laborTypes, setLaborTypes] = useState([]);

  const fetchData = async () => {
    setLoading(true);
    const data = await paginationEstimates({
      page: 0,
      maxSize: true,
      filters: [{ columnJoin: 'project', field: 'id', value: projectId, restriction: 'EQUAL' }],
    });
    if (data.numberOfElements === 0) {
      message.error('Not found');
    } else {
      setEstimateData(data.content[0]);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
    paginationSubVendors({
      page: 0,
      maxSize: true,
      filters: [{ field: 'type', value: 'SUBCONTRACTOR', restriction: 'EQUAL' }],
    }).then(data => {
      setAllSubVendors(data.content);
    });
    paginationLaborTypes({ page: 0, sortBy: 'name', maxSize: true, filters: [] }).then(data => {
      setAllLaborTypes(data.content);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    findAllEstimateSubVendors([
      {
        columnJoin: 'estimateItem',
        columnSecondJoin: 'estimate',
        field: 'id',
        value: estimateData?.id,
        restriction: 'EQUAL',
      },
    ]).then(data => {
      setSelectedEstimateSubVendors(data);
    });
  }, [estimateData]);

  useEffect(() => {
    // set winners with selectedEstimateSubVendors that have winner = true
    const newWinners = [];
    const winnersItems = selectedEstimateSubVendors.filter(estimateSubvendor => estimateSubvendor.winner === true);
    winnersItems.forEach(item => {
      const winner = {};
      winner.subVendorId = item.subVendor.id;
      winner.laborTypeId = item.estimateItem.laborType.id;
      // insert winner in newWinners if not exists
      if (
        !newWinners.find(
          winnerItem => winnerItem.subVendorId === winner.subVendorId && winnerItem.laborTypeId === winner.laborTypeId
        )
      ) {
        newWinners.push(winner);
      }
    });
    if (newWinners.length > 0) {
      setWinners(newWinners);
    }
  }, [selectedEstimateSubVendors]);

  // mount a list of selectedEstimateSubvendors with subVendor and estimateItem data
  const mountEstimateSubvendors = () => {
    const complete = [];
    selectedEstimateSubVendors.forEach(element => {
      element.subVendor = allSubVendors.find(subVendor => subVendor.id === element.subvendor_id); // get data from subvendors by subvendor_id
      element.estimateItem = completeEstimate.items.find(estimateItem => estimateItem.id === element.estimate_item_id); // get data from estimate_items by estimate_item_id
      complete.push(element);
    });
    setSelectedEstimateSubVendors(complete);
  };

  // mount a list of uniques subvendors that are in estimate_subvendors
  const mountSubvendors = () => {
    const listSubvendors = [];
    allSubVendors.forEach(element => {
      if (selectedEstimateSubVendors.some(estimateSubvendor => estimateSubvendor.subvendor_id === element.id)) {
        listSubvendors.push(element);
      }
    });
    setAllSubVendors(listSubvendors);
  };

  // mount a list of uniques laborTypes that are in selectedEstimateSubvendors.estimateItem.laborType
  const mountLaborTypes = () => {
    const listLaborTypes = [];
    allLaborTypes.forEach(element => {
      if (
        selectedEstimateSubVendors.some(estimateSubvendor => estimateSubvendor.estimateItem.laborType.id === element.id)
      ) {
        listLaborTypes.push(element);
      }
    });
    setLaborTypes(listLaborTypes);
  };

  // mount a list subvendors with their items
  const mountCompleteEstimate = () => {
    const complete = [];
    const laborType = laborTypes.find(laborType => laborType.id === showType);
    if (allSubVendors.length === 0 || !laborType) return;
    allSubVendors.forEach(subVendor => {
      const items = [];
      selectedEstimateSubVendors
        .filter(estimateSubvendor => estimateSubvendor.subVendor?.id === subVendor.id)
        .filter(estimateSubvendor => estimateSubvendor.estimateItem.laborType?.id === laborType.id)
        .forEach(estimateSubvendor => {
          const item = {
            id: estimateSubvendor.estimateItem.id,
            description: estimateSubvendor.estimateItem.estimateDescription.description,
            unit: estimateSubvendor.estimateItem.estimateDescription.unit,

            ownQuantity: estimateSubvendor.estimateItem.quantity,
            ownMaterialValue: estimateSubvendor.estimateItem.materialValue,
            ownLaborValue: estimateSubvendor.estimateItem.laborValue,

            quantity: estimateSubvendor.quantity,
            materialValue: estimateSubvendor.materialValue,
            laborValue: estimateSubvendor.laborValue,

            projectId: projectId,
            area: estimateSubvendor.estimateItem.area,
            type: estimateSubvendor.estimateItem.type,
            laborType: estimateSubvendor.estimateItem.laborType,
            dueDate: estimateSubvendor.dueDate,
            note: estimateSubvendor.note,
            winner: estimateSubvendor.winner,
          };
          items.push(item);
        });
      if (items.length > 0) {
        complete.push({ subVendor, items });
      }
    });
    setCompleteEstimate(complete);
  };

  useEffect(() => {
    mountEstimateSubvendors();
    mountSubvendors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedEstimateSubVendors.length > 0 && allLaborTypes.length > 0) mountLaborTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEstimateSubVendors, allLaborTypes]);

  useEffect(() => {
    if (allSubVendors.length > 0 && laborTypes.length > 0) mountCompleteEstimate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allSubVendors, laborTypes, showType]);

  const handleWinner = (idLaborType, idSubvendor) => {
    let findWinner = false;
    const newWinners = winners.filter(winner => winner.laborTypeId !== idLaborType);
    if (!winners.find(winner => winner.laborTypeId === idLaborType && winner.subVendorId === idSubvendor)) {
      const winner = { laborTypeId: idLaborType, subVendorId: idSubvendor };
      findWinner = true;
      newWinners.push(winner);
    }
    setWinners(newWinners);

    const newEstimateSubvendors = selectedEstimateSubVendors.map(estimate => {
      if (estimate.subVendor.id === idSubvendor && estimate.estimateItem.laborType.id === idLaborType) {
        estimate.winner = findWinner;
      }
      if (estimate.subVendor.id !== idSubvendor && estimate.estimateItem.laborType.id === idLaborType) {
        estimate.winner = false;
      }
      return estimate;
    });
    setSelectedEstimateSubVendors(newEstimateSubvendors);
  };

  // Dentro de usePriceQuotesData.js (logo acima do return), adicione:

  // const updateValuesForSubvendor = async (subVendorId, updatedItems) => {
  //   // updatedItems: [{ id: <estimateItemId>, materialValue: number, laborValue: number }, ...]
  //   // 1) Atualizar o estado local
  //   setSelectedEstimateSubVendors(prev =>
  //     prev.map(esv => {
  //       if (esv.subVendor.id === subVendorId && esv.estimateItem.laborType.id === showType) {
  //         // Busca se há um updatedItem que corresponda a este estimateItem.id
  //         const udp = updatedItems.find(ui => ui.id === esv.estimateItem.id);
  //         if (udp) {
  //           return {
  //             ...esv,
  //             materialValue: udp.materialValue,
  //             laborValue: udp.laborValue,
  //           };
  //         }
  //       }
  //       return esv;
  //     })
  //   );

  //   // 2) Enviar para a API cada estimateSubVendor alterado
  //   const itensParaAtualizar = selectedEstimateSubVendors.filter(
  //     esv =>
  //       esv.subVendor.id === subVendorId &&
  //       esv.estimateItem.laborType.id === showType &&
  //       updatedItems.some(ui => ui.id === esv.estimateItem.id)
  //   );

  //   await Promise.all(itensParaAtualizar.map(esv => updateEstimateSubVendor(esv)));
  // };

    const updateValuesForSubvendor = useCallback(
    async (subVendorId, updatedItems) => {
      // updatedItems: [{ id: <estimateItemId>, materialValue, laborValue }, ...]

      // 1) Dispara todas as chamadas de API para atualização do backend
      //    Aqui montamos o objeto completo a partir do estado atual para cada item
      const chamadas = updatedItems.map(ui => {
        // Busca, no estado atual, o objeto completo do estimateSubVendor
        const esvOriginal = selectedEstimateSubVendors.find(
          esv =>
            esv.subVendor.id === subVendorId &&
            esv.estimateItem.id === ui.id
        );

        if (esvOriginal) {
          // Cria uma cópia com os novos valores
          const esvAtualizado = {
            ...esvOriginal,
            materialValue: ui.materialValue,
            laborValue: ui.laborValue,
          };
          // Retorna a Promise da chamada à API
          return updateEstimateSubVendor(esvAtualizado);
        }

        // Caso não encontre (por segurança), retorna uma Promise já resolvida
        return Promise.resolve(null);
      });

      // Aguarda todas as requisições terminarem (podem falhar, tratar erro se quiser)
      await Promise.all(chamadas);

      // 2) Atualiza o estado local **depois** que a API tiver sido chamada
      setSelectedEstimateSubVendors(prev =>
        prev.map(esv => {
          // Só altera quem for do mesmo subvendor e estiver entre os updatedItems
          if (
            esv.subVendor.id === subVendorId &&
            updatedItems.some(ui => ui.id === esv.estimateItem.id)
          ) {
            // Encontra o updatedItem correspondente
            const ui = updatedItems.find(
              ui2 => ui2.id === esv.estimateItem.id
            );
            return {
              ...esv,
              materialValue: ui.materialValue,
              laborValue: ui.laborValue,
            };
          }
          return esv;
        })
      );
    },
    [selectedEstimateSubVendors] // Recria a função se selectedEstimateSubVendors mudar
  );

  const handleSaveWinners = () => {
    const subVendorsWinners = selectedEstimateSubVendors.filter(estimateSubvendor => estimateSubvendor.winner === true);
    subVendorsWinners.forEach(async subVendorWinner => {
      await updateEstimateSubVendor(subVendorWinner);
    });
  };

  return {
    loading,
    laborTypes,
    completeEstimate,
    winners,
    showType,
    setShowType,
    handleWinner,
    updateValuesForSubvendor, 
    handleSaveWinners,
  };
}
